import {
  TTS_DEFAULT_SPEED,
  TTS_DEFAULT_SPEAKER_ID,
  TTS_DEFAULT_LANG_CODE,
  TTS_DEFAULT_INSTRUCTION,
  TTS_BULK_DEFAULT_SPEED,
  TTS_DEFAULT_BULK_LANG_CODE,
  TTS_DEFAULT_BULK_SPEAKER_ID,
  TTS_DEFAULT_BULK_INSTRUCTION
} from 'consts/tts'
import { generateRandomNumberId } from 'utils'
import { ITtsItem, ITtsItemData } from 'types/text-to-speech'
import {
  IDraftTtsItemProps,
  INewGeneratedItemsInitialIdAndStatus,
  TTtsItemKey,
  TTtsItemStateKey
} from 'store/types/tts'
import { IVoiceSpeakersPerLangCode } from 'store/types/voiceCatalog'
import { getTtsItems_API } from 'api/tts'
import { checkIsPremiumVoice } from 'utils/tts'

// to create a new item with draft props
export const createTtsItemWithDraftProps = (data: ITtsItemData): ITtsItem => ({
  data: { ...data },
  isGenerated: true,
  isLoading: false,
  isDraft: true
})

const getDefaultTtsAudio = (
  id: number = generateRandomNumberId(),
  speakerId: number,
  speed: number,
  langCode: string,
  instructions: string
): ITtsItemData => ({
  id,
  userId: 0,
  text: '',
  filePath: '',
  duration: 0,
  speakerId,
  speed,
  langCode,
  instructions
})

//only for draft new item
export const getDraftTtsBlock = ({
  id,
  speakerId = TTS_DEFAULT_SPEAKER_ID,
  speed = TTS_DEFAULT_SPEED,
  langCode = TTS_DEFAULT_LANG_CODE,
  instructions = TTS_DEFAULT_INSTRUCTION
}: IDraftTtsItemProps): ITtsItem => {
  return createTtsItemWithDraftProps({
    ...getDefaultTtsAudio(id, speakerId, speed, langCode, instructions)
  })
}

//to add a new draft itemw ith the previous item's data (speaker, lang, speed)
export const getDraftTtsItem = (
  speakerId?: number,
  speed?: number,
  langCode?: string,
  instructions?: string
) => [getDraftTtsBlock({ speakerId, speed, langCode, instructions })]

export const findTtsItemById = (items: ITtsItem[], id: number | string) =>
  items.find(item => item.data.id === id)

export const getIsTtsItemUnchanged = ({
  ttsItem,
  initialTtsItems,
  speakerId,
  speed,
  langCode,
  instructions
}: {
  ttsItem: ITtsItem
  initialTtsItems: ITtsItem[]
  speakerId: number
  speed: number
  langCode: string
  instructions: string
}) => {
  const isTextEmpty = ttsItem?.data.text === ''

  //if the text is already empty, we don't need to check the other values
  if (isTextEmpty) {
    return true
  }

  const initialTtsItem = initialTtsItems.find(
    initialTtsItem => initialTtsItem.data.id === ttsItem.data.id
  )?.data

  if (!initialTtsItem) {
    return false
  }

  const {
    text: initialText,
    speakerId: initialSpeakerId,
    speed: initialSpeed,
    langCode: initialLangCode,
    instructions: initialInstruction
  } = initialTtsItem

  //we check text by this way because the text is not connected with bulk action
  const isTextUnchanged = ttsItem?.data.text === initialText

  const isSpeakerIdUnchanged =
    speakerId !== TTS_DEFAULT_BULK_SPEAKER_ID
      ? initialSpeakerId === speakerId
      : true

  const isSpeedUnchanged =
    speed !== TTS_BULK_DEFAULT_SPEED ? initialSpeed === speed : true

  const isLangCodeUnchanged =
    langCode !== TTS_DEFAULT_BULK_LANG_CODE
      ? initialLangCode === langCode
      : true

  const isInstructionUnchanged =
    instructions !== TTS_DEFAULT_BULK_INSTRUCTION
      ? initialInstruction === instructions
      : true

  return (
    isTextUnchanged &&
    isSpeakerIdUnchanged &&
    isSpeedUnchanged &&
    isLangCodeUnchanged &&
    isInstructionUnchanged
  )
}

export const determineBulkValue = <T>({
  isMixed,
  lastValue,
  defaultValue
}: {
  isMixed: boolean
  lastValue?: T
  defaultValue: T
}): T => {
  return isMixed ? defaultValue : lastValue || defaultValue
}

export const setTtsItemValueByKey = ({
  mutableTtsItems,
  key,
  id,
  value
}: {
  mutableTtsItems: ITtsItem[]
  key: TTtsItemKey
  id: number | string
  value: string | number
}): void => {
  const itemIndex = mutableTtsItems.findIndex(item => item.data.id === id)

  if (itemIndex !== -1) {
    mutableTtsItems[itemIndex] = {
      ...mutableTtsItems[itemIndex],
      data: {
        ...mutableTtsItems[itemIndex].data,
        [key]: value
      }
    }
  }
}

export const checkTtsItemGeneratedState = ({
  unchangedItems,
  ttsItem
}: {
  unchangedItems: number[]
  ttsItem: ITtsItem
}) => {
  if (unchangedItems) {
    const { data } = ttsItem
    const isUnchangedTtsItemId = unchangedItems.some(id => id === data.id)
    return isUnchangedTtsItemId
  }

  return false
}
//for state not data (e.g. isGenerated, isDraft, isLoading)
export const setTtsItemStateByKey = ({
  mutableTtsItems,
  stateKey,
  id,
  value
}: {
  mutableTtsItems: ITtsItem[]
  stateKey: TTtsItemStateKey
  id: number | string
  value: boolean
}): void => {
  const itemIndex = mutableTtsItems.findIndex(item => item.data.id === id)

  if (itemIndex !== -1) {
    mutableTtsItems[itemIndex] = {
      ...mutableTtsItems[itemIndex],
      [stateKey]: value
    }
  }
}

export const removeDraftEmptyItems = (
  mutableTtsItems: ITtsItem[]
): ITtsItem[] => {
  return mutableTtsItems.filter(item => !item.isDraft || item.data.text !== '')
}

export const bulkUpdateTtsValuesByKey = ({
  mutableTtsItems,
  key,
  value,
  unchangedItems
}: {
  mutableTtsItems: ITtsItem[]
  key: TTtsItemKey
  value: string | number
  unchangedItems: number[]
}): void => {
  mutableTtsItems.forEach(item => {
    setTtsItemValueByKey({
      mutableTtsItems,
      key,
      id: item.data.id,
      value
    })
    setTtsItemStateByKey({
      mutableTtsItems,
      stateKey: 'isGenerated',
      id: item.data.id,
      value: checkTtsItemGeneratedState({
        unchangedItems,
        ttsItem: item
      })
    })
  })
}

//if ttsAudio or ttsItems  are changed, we need to fetch the new items,
// however, we don't need to fetch the items that are not changed, so need this check to filter out the items that are not changed
export const getItemsToFetch = (sortedIds: number[], unchanged: ITtsItem[]) => {
  const unchangedIds = new Set(unchanged.map(item => item.data.id))
  return sortedIds.filter(id => !unchangedIds.has(id))
}

export const fetchTtsItemsByIds = async (
  ids: number[]
): Promise<ITtsItemData[]> => {
  if (!ids.length) return []
  const response = await getTtsItems_API(ids)
  return response.data
}

export const getIsOrderChanged = (
  items: ITtsItemData[],
  sortedIds: number[]
) => {
  return items.some((item, index) => item.id !== sortedIds[index])
}

// as we call this sync action for all types of changes with ttsAudios,
// (first initial fetch of tts items by tts auduis,
// new generation => sync, ttsaudios timline changes -> sync, delete from tts tab ot timeline -> sync)
// we need to check if we need to sync for any of this cases
export const determineShouldReSync = ({
  allGeneratedItemsData,
  sortedTtsAudiosIds,
  newGeneratedItemsInitialIdAndStatus,
  newItems
}: {
  allGeneratedItemsData: ITtsItemData[]
  sortedTtsAudiosIds: number[]
  newGeneratedItemsInitialIdAndStatus: INewGeneratedItemsInitialIdAndStatus[]
  newItems: ITtsItemData[]
}): boolean => {
  return (
    getIsOrderChanged(allGeneratedItemsData, sortedTtsAudiosIds) ||
    newGeneratedItemsInitialIdAndStatus.length > 0 ||
    newItems.length > 0
  )
}

const filterSuccessItems = (
  items: ITtsItem[],
  generatedInitialIds: INewGeneratedItemsInitialIdAndStatus[]
) => {
  return items.filter(item => {
    const match = generatedInitialIds.find(entry => entry.id === item.data.id)
    return !match || match.status === 'failed'
  })
}

// if we have pending items, we need to merge them with the reordered items keeping the draft changes
// in case of it is not synced after generation action, but just a tts audio change or if the generation is failed we need to keep the draft info
export const mergeAllItems = (
  reorderedItems: ITtsItem[],
  pendingItems: ITtsItem[],
  newGeneratedItemsInitialIdAndStatus: INewGeneratedItemsInitialIdAndStatus[]
): ITtsItem[] => {
  const pendingMap = new Map(
    filterSuccessItems(pendingItems, newGeneratedItemsInitialIdAndStatus).map(
      item => [item.data.id, item]
    )
  )

  const merged = reorderedItems.map(item => {
    const pending = pendingMap.get(item.data.id)

    if (pending) {
      pendingMap.delete(item.data.id)
      return pending
    }
    return item
  })

  return [...merged, ...pendingMap.values()]
}

export const checkIfPremiumVoiceSelected = (
  ttsItem: ITtsItem,
  speakersPerLangCode: IVoiceSpeakersPerLangCode | null
): boolean => {
  if (!speakersPerLangCode) {
    return false
  }

  const { langCode, speakerId } = ttsItem.data
  const speakers = speakersPerLangCode[langCode]?.data || []

  return checkIsPremiumVoice(speakerId, speakers)
}
